import { Schema, Option, Stream, Effect } from 'effect';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var TextRange = Schema.Struct({ from: Schema.Number, to: Schema.Number });
var SemanticToken = Schema.Struct({ from: Schema.Number, to: Schema.Number, kind: Schema.String });
var Severity = Schema.Literals(["error", "warning", "info", "hint"]);
var Diagnostic = Schema.Struct({
  from: Schema.Number,
  to: Schema.Number,
  severity: Severity,
  message: Schema.String,
  code: Schema.optional(Schema.String)
});
var SEVERITIES = ["error", "warning", "info", "hint"];
var mostSevere = (diagnostics) => diagnostics.reduce(
  (worst, diagnostic) => worst === void 0 || SEVERITIES.indexOf(diagnostic.severity) < SEVERITIES.indexOf(worst.severity) ? diagnostic : worst,
  void 0
);
var diagnosticsAt = (diagnostics, offset) => diagnostics.filter(
  (diagnostic) => diagnostic.from === diagnostic.to ? offset === diagnostic.from : offset >= diagnostic.from && offset < diagnostic.to
);
var CompletionItem = Schema.Struct({
  label: Schema.String,
  /** The text that replaces the completion's range. Defaults to `label`. */
  insert: Schema.optional(Schema.String),
  /** Secondary text, such as a type, a signature, or where the item comes from. */
  detail: Schema.optional(Schema.String),
  /** Exposed as `data-kind` on the option, such as `function` or `person`. */
  kind: Schema.optional(Schema.String),
  /** The text typing is matched against. Defaults to `label`. */
  filterText: Schema.optional(Schema.String)
});
var HoverSource = Schema.Literals(["Pointer", "Keyboard"]);

// src/completion.ts
var completion_exports = {};
__export(completion_exports, {
  List: () => List,
  accept: () => accept,
  active: () => active,
  contains: () => contains,
  matching: () => matching,
  move: () => move,
  open: () => open,
  query: () => query,
  track: () => track,
  visible: () => visible,
  wordBefore: () => wordBefore
});
var List = Schema.Struct({
  from: Schema.Number,
  to: Schema.Number,
  items: Schema.Array(CompletionItem),
  index: Schema.Number
});
var open = (from, to, items) => ({
  from,
  to,
  items,
  index: 0
});
var matching = (items, query2) => {
  const wanted = query2.normalize("NFC").toLowerCase();
  if (wanted === "") return items;
  const key2 = (item) => (item.filterText ?? item.label).normalize("NFC").toLowerCase();
  return [
    ...items.filter((item) => key2(item).startsWith(wanted)),
    ...items.filter((item) => !key2(item).startsWith(wanted) && key2(item).includes(wanted))
  ];
};
var contains = (completion, caret) => caret >= completion.from && caret <= completion.to;
var query = (completion, text, caret) => text.slice(completion.from, Math.max(completion.from, caret));
var visible = (completion, text, caret) => {
  if (!contains(completion, caret) || completion.to > text.length) return [];
  const typed = query(completion, text, caret);
  return matching(completion.items, typed).filter(
    (item) => caret < completion.to || item.label !== typed && (item.insert ?? item.label) !== typed
  );
};
var active = (completion, text, caret) => {
  const items = visible(completion, text, caret);
  return items[Math.min(completion.index, items.length - 1)];
};
var move = (completion, delta, count) => count === 0 ? completion : {
  ...completion,
  index: ((Math.min(completion.index, count - 1) + delta) % count + count) % count
};
var change = (before, after, caret) => {
  if (before === after) return void 0;
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) {
    start += 1;
  }
  const delta = after.length - before.length;
  return { start: Math.min(start, caret - Math.max(0, delta)), delta };
};
var track = (completion, before, after, caret) => {
  const edit = change(before, after, caret);
  if (edit === void 0) return contains(completion, caret) ? completion : void 0;
  if (edit.start < completion.from || edit.start > completion.to) return void 0;
  const next = {
    ...completion,
    to: Math.max(completion.from, completion.to + edit.delta),
    index: 0
  };
  return contains(next, caret) && next.to <= after.length ? next : void 0;
};
var accept = (completion, text, item) => {
  const insert = item.insert ?? item.label;
  const to = Math.min(completion.to, text.length);
  return {
    text: text.slice(0, completion.from) + insert + text.slice(to),
    caret: completion.from + insert.length,
    from: completion.from,
    to,
    insert
  };
};
var wordBefore = (text, offset, pattern = /[\p{L}\p{N}_$-]+$/u) => {
  const match = pattern.exec(text.slice(0, offset));
  const word = match?.[0] ?? "";
  return { from: offset - word.length, word };
};

// src/segments.ts
var segments = (text, layers, offset = 0) => {
  const end = text.length;
  const clip = (value) => Math.max(0, Math.min(end, value - offset));
  const names = Object.keys(layers);
  const relevant = names.map(
    (name) => [
      name,
      layers[name].filter((range) => range.to - offset >= 0 && range.from - offset <= end)
    ]
  );
  const cuts = /* @__PURE__ */ new Set([0, end]);
  for (const [, ranges] of relevant) {
    for (const range of ranges) {
      cuts.add(clip(range.from));
      cuts.add(clip(range.to));
    }
  }
  const positions = [...cuts].sort((a, b) => a - b);
  const result = [];
  for (let index = 0; index < positions.length - 1; index += 1) {
    const from = positions[index];
    const to = positions[index + 1];
    const covering = Object.fromEntries(
      relevant.map(([name, ranges]) => [
        name,
        ranges.filter((range) => clip(range.from) <= from && clip(range.to) >= to)
      ])
    );
    result.push({ from, to, text: text.slice(from, to), covering });
  }
  return result;
};

// src/geometry.ts
var SKIP = "[data-text-skip]";
var pieces = (root) => {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const result = [];
  let start = 0;
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const skipped = node.parentElement?.closest(SKIP);
    if (skipped !== null && skipped !== void 0 && root.contains(skipped)) continue;
    const text = node;
    result.push({ node: text, start });
    start += text.data.length;
  }
  return result;
};
var contains2 = (rect, x, y) => x >= rect.left && x < rect.right && y >= rect.top && y < rect.bottom;
var offsetAtPoint = (root, x, y) => {
  const doc = root.ownerDocument;
  const range = doc.createRange();
  for (const { node, start } of pieces(root)) {
    const length = node.data.length;
    if (length === 0) continue;
    range.selectNodeContents(node);
    const bounds = [...range.getClientRects()];
    if (!bounds.some((rect) => contains2(rect, x, y))) continue;
    for (let index = 0; index < length; index += 1) {
      const code = node.data.charCodeAt(index);
      const size = code >= 55296 && code <= 56319 && index + 1 < length ? 2 : 1;
      range.setStart(node, index);
      range.setEnd(node, index + size);
      if ([...range.getClientRects()].some((rect) => contains2(rect, x, y))) {
        return start + index;
      }
      index += size - 1;
    }
  }
  return void 0;
};
var rectAtOffset = (root, offset) => {
  const doc = root.ownerDocument;
  const all = pieces(root);
  const range = doc.createRange();
  const inside = all.find(
    ({ node, start }) => offset >= start && offset < start + node.data.length
  );
  for (const { node, start } of inside === void 0 ? all : [inside]) {
    const length = node.data.length;
    if (offset < start || offset > start + length) continue;
    const local = offset - start;
    if (local < length) {
      range.setStart(node, local);
      range.setEnd(node, local + 1);
      const rect = range.getClientRects()[0];
      if (rect !== void 0) return new DOMRect(rect.left, rect.top, 0, rect.height);
    } else if (length > 0) {
      range.setStart(node, length - 1);
      range.setEnd(node, length);
      const rects = range.getClientRects();
      const rect = rects[rects.length - 1];
      if (rect !== void 0) return new DOMRect(rect.right, rect.top, 0, rect.height);
    }
  }
  const box = root.getBoundingClientRect();
  return all.length === 0 ? new DOMRect(box.left, box.top, 0, box.height) : void 0;
};
var rangeRects = (root, from, to) => {
  const doc = root.ownerDocument;
  const range = doc.createRange();
  const result = [];
  for (const { node, start } of pieces(root)) {
    const length = node.data.length;
    const localFrom = Math.max(0, from - start);
    const localTo = Math.min(length, to - start);
    if (localFrom >= localTo) continue;
    range.setStart(node, localFrom);
    range.setEnd(node, localTo);
    result.push(...range.getClientRects());
  }
  return result;
};
var isOver = (rects, x, y) => rects.some((rect) => contains2(rect, x, y));
var Anchor = Schema.Struct({ selector: Schema.String, offset: Schema.Number });
var GAP = 4;
var visibleBounds = (element) => {
  const view = element.ownerDocument.defaultView;
  let top = 0;
  let left = 0;
  let bottom = view?.innerHeight ?? Infinity;
  let right = view?.innerWidth ?? Infinity;
  for (let node = element.parentElement; node !== null; node = node.parentElement) {
    const style = view?.getComputedStyle(node);
    if (style === void 0 || `${style.overflowX}${style.overflowY}` === "visiblevisible")
      continue;
    const rect = node.getBoundingClientRect();
    top = Math.max(top, rect.top);
    left = Math.max(left, rect.left);
    bottom = Math.min(bottom, rect.bottom);
    right = Math.min(right, rect.right);
  }
  return { top, bottom, left, right };
};
var anchorPopup = (args) => ({
  name: "AnchorTextPopup",
  args,
  f: (element) => Stream.callback(
    () => Effect.acquireRelease(
      Effect.sync(() => {
        const { selector, offset, keepFocus } = args;
        const popup = element;
        const doc = popup.ownerDocument;
        const view = doc.defaultView;
        let pending = 0;
        const place = () => {
          const root = doc.querySelector(selector);
          const at = root === null ? void 0 : rectAtOffset(root, offset);
          if (root === null || at === void 0) {
            popup.style.visibility = "hidden";
            return;
          }
          const shown = visibleBounds(root);
          const inView = at.bottom > shown.top && at.top < shown.bottom && at.left >= shown.left - 1 && at.left <= shown.right;
          popup.style.visibility = inView ? "" : "hidden";
          const viewportHeight = view?.innerHeight ?? Infinity;
          const viewportWidth = view?.innerWidth ?? Infinity;
          const height = popup.offsetHeight;
          const width = popup.offsetWidth;
          const below = at.bottom + GAP;
          const flip = below + height > viewportHeight && at.top - GAP - height >= 0;
          const top = flip ? at.top - GAP - height : below;
          const left = Math.max(4, Math.min(at.left, viewportWidth - width - 4));
          popup.style.left = `${left}px`;
          popup.style.top = `${top}px`;
          const placed = popup.getBoundingClientRect();
          if (Math.abs(placed.left - left) > 0.5 || Math.abs(placed.top - top) > 0.5) {
            popup.style.left = `${2 * left - placed.left}px`;
            popup.style.top = `${2 * top - placed.top}px`;
          }
          popup.dataset.placement = flip ? "Above" : "Below";
          const active2 = popup.querySelector('[aria-selected="true"]');
          active2?.scrollIntoView({ block: "nearest" });
        };
        const schedule = () => {
          cancelAnimationFrame(pending);
          pending = requestAnimationFrame(place);
        };
        const press = (event) => {
          if (keepFocus) event.preventDefault();
        };
        place();
        const resize = new ResizeObserver(schedule);
        resize.observe(popup);
        const mutations = new MutationObserver(schedule);
        mutations.observe(popup, {
          subtree: true,
          attributes: true,
          attributeFilter: ["aria-selected"]
        });
        doc.addEventListener("scroll", schedule, { capture: true, passive: true });
        view?.addEventListener("resize", schedule);
        popup.addEventListener("pointerdown", press);
        popup.addEventListener("mousedown", press);
        return () => {
          cancelAnimationFrame(pending);
          resize.disconnect();
          mutations.disconnect();
          doc.removeEventListener("scroll", schedule, { capture: true });
          view?.removeEventListener("resize", schedule);
          popup.removeEventListener("pointerdown", press);
          popup.removeEventListener("mousedown", press);
        };
      }),
      (dispose) => Effect.sync(dispose)
    ).pipe(Effect.andThen(Effect.never))
  )
});
var optionId = (listId, index) => `${listId}-option-${index}`;
var emphasize = (label, query2, h) => {
  const at = query2 === "" ? -1 : label.toLowerCase().indexOf(query2.toLowerCase());
  return at < 0 ? [label] : [
    label.slice(0, at),
    h.mark([h.Class("fw-completion__match")], [label.slice(at, at + query2.length)]),
    label.slice(at + query2.length)
  ];
};
var CompletionPopup = {
  view: (config, h) => h.keyed("div")(
    `${config.id}:${config.anchor.selector}:${config.anchor.offset}`,
    [
      h.Class("fw-text-popup fw-completion"),
      h.DataAttribute("text-popup", "completion"),
      h.OnMount(anchorPopup({ ...config.anchor, keepFocus: true }))
    ],
    [
      h.ul(
        [h.Id(config.id), h.Role("listbox"), h.AriaLabel(config.label ?? "Suggestions")],
        config.items.map(
          (item, index) => h.li(
            [
              h.Id(optionId(config.id, index)),
              h.Class("fw-completion__option"),
              h.Role("option"),
              h.AriaSelected(index === config.index),
              ...item.kind === void 0 ? [] : [h.DataAttribute("kind", item.kind)],
              h.OnClick(config.onChoose(index))
            ],
            [
              h.span([h.Class("fw-completion__kind"), h.AriaHidden(true)], []),
              h.span(
                [h.Class("fw-completion__label")],
                emphasize(item.label, config.query ?? "", h)
              ),
              ...item.detail === void 0 ? [] : [h.span([h.Class("fw-completion__detail")], [item.detail])]
            ]
          )
        )
      )
    ]
  )
};
var severityLabel = {
  error: "Error",
  warning: "Warning",
  info: "Info",
  hint: "Hint"
};
var HoverPopup = {
  view: (config, h) => {
    const diagnostics = [...config.diagnostics ?? []].sort(
      (a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity)
    );
    return h.keyed("div")(
      `${config.id}:${config.anchor.selector}:${config.anchor.offset}`,
      [
        h.Id(config.id),
        h.Class("fw-text-popup fw-hover"),
        h.Role("tooltip"),
        h.DataAttribute("text-popup", "hover"),
        h.OnMount(anchorPopup({ ...config.anchor, keepFocus: false }))
      ],
      [
        ...diagnostics.length === 0 ? [] : [
          h.ul(
            [h.Class("fw-hover__diagnostics")],
            diagnostics.map(
              (diagnostic) => h.li(
                [
                  h.Class("fw-hover__diagnostic"),
                  h.DataAttribute("severity", diagnostic.severity)
                ],
                [
                  h.span([h.Class("fw-hover__severity")], [severityLabel[diagnostic.severity]]),
                  " ",
                  diagnostic.message,
                  ...diagnostic.code === void 0 ? [] : [" ", h.code([h.Class("fw-hover__code")], [diagnostic.code])]
                ]
              )
            )
          )
        ],
        ...config.content === null ? [] : [h.div([h.Class("fw-hover__content")], [config.content])]
      ]
    );
  }
};

// src/mentions.ts
var mentions_exports = {};
__export(mentions_exports, {
  Entity: () => Entity,
  Kind: () => Kind,
  analyze: () => analyze,
  describe: () => describe,
  descriptionView: () => descriptionView,
  markdownOptions: () => markdownOptions,
  queryAt: () => queryAt,
  recognize: () => recognize,
  resolve: () => resolve,
  suggestions: () => suggestions,
  tagsIn: () => tagsIn
});
var Kind = Schema.Literals(["mention", "tag"]);
var Entity = Schema.Struct({
  id: Schema.String,
  kind: Kind,
  name: Schema.String,
  label: Schema.String,
  description: Schema.optional(Schema.String)
});
var key = (value) => value.normalize("NFC").toLowerCase();
var marker = (kind) => kind === "mention" ? "@" : "#";
var namePattern = /^[\p{L}\p{N}_][\p{L}\p{M}\p{N}_-]*$/u;
var boundary = (before) => before === "" || /[\s([{,;:!?]/u.test(before);
var allowed = (text, from, to, options) => (options.boundary ?? boundary)(text.slice(0, from).at(-1) ?? "", from, text) && !(options.excluded ?? []).some(
  (range) => range.from <= from && from < range.to || range.from < to && to <= range.to || from <= range.from && to >= range.to
);
var scan = (text, options, empty) => {
  const result = [];
  for (const match of text.matchAll(/[@#][\p{L}\p{M}\p{N}_-]*/gu)) {
    const from = match.index;
    const name = match[0].slice(1);
    const to = from + match[0].length;
    if ((empty && name === "" || namePattern.test(name)) && allowed(text, from, to, options))
      result.push({ from, to, kind: match[0][0] === "@" ? "mention" : "tag", name });
  }
  return result;
};
var recognize = (text, options = {}) => scan(text, options, false);
var queryAt = (text, caret, options = {}) => {
  if (!Number.isInteger(caret) || caret < 0 || caret > text.length || /[\uD800-\uDBFF]/u.test(text[caret - 1] ?? "") && /[\uDC00-\uDFFF]/u.test(text[caret] ?? ""))
    return void 0;
  const mark = scan(text, options, true).find((mark2) => mark2.from < caret && caret <= mark2.to);
  return mark === void 0 ? void 0 : { ...mark, caret, text: text.slice(mark.from, caret) };
};
var entities = (data) => {
  const result = /* @__PURE__ */ new Map();
  for (const entity of data.entities) {
    if (namePattern.test(entity.name)) result.set(`${entity.kind}:${key(entity.name)}`, entity);
  }
  for (const name of data.tags ?? []) {
    const id = `tag:${key(name)}`;
    if (namePattern.test(name) && !result.has(id))
      result.set(id, {
        id,
        kind: "tag",
        name: key(name),
        label: `#${key(name)}`,
        description: "Tag"
      });
  }
  return [...result.values()];
};
var resolve = (mark, data) => entities(data).find((entity) => entity.kind === mark.kind && key(entity.name) === key(mark.name));
var tagsIn = (texts, options = {}) => [
  ...new Set(
    texts.flatMap(
      (text) => recognize(text, options).filter((mark) => mark.kind === "tag").map((mark) => key(mark.name))
    )
  )
].sort();
var suggestions = (query2, text, data) => {
  const suffix = query2.to === text.length ? " " : "";
  const items = entities(data).filter((entity) => entity.kind === query2.kind).map((entity) => ({
    label: `${marker(entity.kind)}${entity.name}`,
    insert: `${marker(entity.kind)}${entity.name}${suffix}`,
    ...entity.label === `${marker(entity.kind)}${entity.name}` ? {} : { detail: entity.label },
    kind: entity.kind,
    filterText: `${marker(entity.kind)}${entity.name} ${entity.label} ${entity.label.split(/\s+/u).map((word) => `${marker(entity.kind)}${word}`).join(" ")}`
  }));
  return open(query2.from, query2.to, items);
};
var analyze = (text, data, options = {}) => {
  const marks = recognize(text, options);
  return {
    tokens: marks.map(({ from, to, kind }) => ({ from, to, kind })),
    diagnostics: data.unknownMention === void 0 ? [] : marks.filter((mark) => mark.kind === "mention" && resolve(mark, data) === void 0).map((mark) => ({
      from: mark.from,
      to: mark.to,
      severity: "warning",
      message: data.unknownMention(mark.name)
    }))
  };
};
var describe = (text, offset, data, options = {}, lenient = false) => {
  const mark = recognize(text, options).find(
    (mark2) => mark2.from <= offset && offset < mark2.to || lenient && offset === mark2.to
  );
  if (!mark) return void 0;
  const entity = resolve(mark, data);
  if (!entity) return void 0;
  return {
    ...mark,
    entity,
    title: entity.label,
    detail: entity.description ?? (entity.kind === "tag" ? "Tag" : `@${entity.name}`)
  };
};
var descriptionView = (description, h) => h.div(
  [h.Class("fw-reference-card")],
  [h.strong([], [description.title]), h.span([], [description.detail])]
);
var markdownOptions = (text) => {
  const excluded = [];
  let fence;
  let at = 0;
  for (const line of text.split(/(?<=\n)/u)) {
    const match = /^ {0,3}(`{3,}|~{3,})/u.exec(line);
    if (fence) {
      if (match && match[1][0] === fence.marker && match[1].length >= fence.length && /^\s*$/u.test(line.slice(match[0].length))) {
        excluded.push({ from: fence.from, to: at + line.length });
        fence = void 0;
      }
    } else if (match) fence = { marker: match[1][0], length: match[1].length, from: at };
    else if (/^ {0,3}#{1,6}(?:\s|$)/u.test(line) || /^(?: {4}|\t)/u.test(line))
      excluded.push({ from: at, to: at + line.length });
    at += line.length;
  }
  if (fence) excluded.push({ from: fence.from, to: text.length });
  for (const match of text.matchAll(/`+/gu)) {
    if (excluded.some((range) => range.from <= match.index && match.index < range.to)) continue;
    const delimiter = match[0];
    const after = match.index + delimiter.length;
    let end = text.indexOf(delimiter, after);
    while (end >= 0 && (text[end - 1] === "`" || text[end + delimiter.length] === "`"))
      end = text.indexOf(delimiter, end + delimiter.length);
    if (end >= 0) excluded.push({ from: match.index, to: end + delimiter.length });
  }
  for (const match of text.matchAll(
    /(?:https?:\/\/|mailto:|www\.)[^\s<>]+|[\p{L}\p{N}_.+-]+@[\p{L}\p{N}.-]+|<[^>\n]*>|\]\([^\n)]*\)/giu
  ))
    excluded.push({ from: match.index, to: match.index + match[0].length });
  return {
    excluded,
    boundary: (before, from, source) => {
      let escapes = 0;
      for (let index = from - 1; source[index] === "\\"; index--) escapes++;
      return escapes % 2 === 0 && boundary(before);
    }
  };
};
var validRanges = (text, ranges) => ranges.filter(
  (range) => Number.isFinite(range.from) && Number.isFinite(range.to) && range.to > range.from && range.to > 0 && range.from < text.length
).map((range) => ({
  ...range,
  from: Math.max(0, Math.floor(range.from)),
  to: Math.min(text.length, Math.ceil(range.to))
}));
var innermostRange = (ranges) => ranges.reduce(
  (best, range) => best === void 0 || range.to - range.from < best.to - best.from ? range : best,
  void 0
);
var HighlightedText = {
  view: (config, h) => {
    const ranges = validRanges(config.text, config.ranges ?? []);
    const pieces2 = segments(config.text, { ranges });
    const first = /* @__PURE__ */ new Map();
    for (const piece of pieces2) {
      const range = innermostRange(piece.covering.ranges);
      if (range !== void 0 && !first.has(range.key)) first.set(range.key, piece.from);
    }
    const controls = ranges.filter((range) => range.onClick !== void 0 && !first.has(range.key));
    return h.div(
      [
        h.Class("fw-highlighted-text"),
        ...config.label === void 0 ? [] : [h.AriaLabel(config.label)]
      ],
      [
        ...pieces2.flatMap((piece) => {
          const range = innermostRange(piece.covering.ranges);
          const hidden = controls.filter((control) => Math.max(0, control.from) === piece.from).map(
            (control) => h.button(
              [
                h.Type("button"),
                h.Class("fw-highlighted-text__hidden-control"),
                h.DataAttribute("highlight-key", control.key),
                h.OnClick(control.onClick),
                h.AriaLabel(
                  control.label ?? config.text.slice(Math.max(0, control.from), control.to)
                )
              ],
              [control.label ?? control.key]
            )
          );
          if (range === void 0) return [...hidden, h.span([], [piece.text])];
          const attrs = [
            h.Class("fw-highlighted-text__range"),
            h.DataAttribute("highlight-key", range.key),
            h.DataAttribute("tone", range.tone ?? "info"),
            h.DataAttribute("depth", String(piece.covering.ranges.length)),
            h.DataAttribute(
              "selected",
              piece.covering.ranges.some((item) => item.key === config.selectedKey) ? "true" : "false"
            ),
            ...range.tooltip === void 0 ? [] : [h.AriaDescription(range.tooltip)]
          ];
          const painted = range.onClick === void 0 ? h.mark(attrs, [piece.text]) : h.span(
            [
              ...attrs,
              h.Role("button"),
              h.Tabindex(first.get(range.key) === piece.from ? 0 : -1),
              h.AriaLabel(range.label ?? config.text.slice(range.from, range.to)),
              h.AriaPressed(range.key === config.selectedKey ? "true" : "false"),
              h.OnClick(range.onClick),
              h.OnKeyDownPreventDefault(
                (key2) => key2 === "Enter" || key2 === " " ? Option.some(range.onClick) : Option.none()
              )
            ],
            [piece.text]
          );
          return [
            ...hidden,
            range.tooltip === void 0 ? painted : h.span(
              [h.Class("fw-highlighted-text__group")],
              [
                painted,
                h.span(
                  [
                    h.Class("fw-highlighted-text__tooltip"),
                    h.Role("tooltip"),
                    h.DataAttribute("text-skip", "true")
                  ],
                  [range.tooltip]
                )
              ]
            )
          ];
        })
      ]
    );
  }
};
var snippets = (text, ranges, context = 40) => {
  const padding = Number.isFinite(context) ? Math.max(0, Math.floor(context)) : 40;
  const clipped = ranges.filter(
    (range) => Number.isFinite(range.from) && Number.isFinite(range.to) && range.to > range.from && range.from < text.length && range.to > 0
  ).map((range) => ({
    from: Math.max(0, Math.floor(range.from)),
    to: Math.min(text.length, Math.ceil(range.to))
  }));
  const windows = [];
  for (const range of [...clipped].sort((a, b) => a.from - b.from)) {
    const from = Math.max(0, range.from - padding);
    const to = Math.min(text.length, range.to + padding);
    const previous = windows.at(-1);
    if (previous !== void 0 && from <= previous.to) previous.to = Math.max(previous.to, to);
    else windows.push({ from, to });
  }
  return windows.map(({ from, to }) => ({
    from,
    to,
    text: text.slice(from, to),
    leadingEllipsis: from > 0,
    trailingEllipsis: to < text.length,
    ranges: clipped.filter((range) => range.from < to && range.to > from)
  }));
};

export { Anchor, completion_exports as Completion, CompletionItem, CompletionPopup, Diagnostic, HighlightedText, HoverPopup, HoverSource, mentions_exports as Mentions, SEVERITIES, SemanticToken, Severity, TextRange, diagnosticsAt, innermostRange, isOver, mostSevere, offsetAtPoint, optionId, rangeRects, rectAtOffset, segments, snippets };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map